import { useMemo } from "react";
import { useAccess } from "../../../../../utils/hooks/access";
import { useActionsColumn } from "./useActionsColumn";
import { useAddColumn } from "./useAddColumn";
import { useCustomFieldsColumns } from "./useCustomFieldsColumns";
import { useDateColumn } from "./useDateColumn";
import { useDateDurationColumn } from "./useDateDurationColumn";
import { useDateGroupActionColumn } from "./useDateGroupActionColumn";
import { useDateTotalColumn } from "./useDateTotalColumn";
import { useDurationColumn } from "./useDurationColumn";
import { useGroupsColumns } from "./useGroupsColumns";
import { useHourTypeColumn } from "./useHourTypeColumn";
import { useInColumn } from "./useInColumn";
import { useJobColumn } from "./useJobColumn";
import { useLocationColumn } from "./useLocationColumn";
import { useOtherDurationColumn } from "./useOtherDurationColumn";
import { useOutColumn } from "./useOutColumn";
import { useOvernightColumn } from "./useOvernightColumn";
import { usePayCodeColumn } from "./usePayCodeColumn";
import { usePayCodeDescriptionColumn } from "./usePayCodeDescriptionColumn";
import { useRateColumn } from "./useRateColumn";
import { useScheduledAmountColumn } from "./useScheduledAmountColumn";
import { useScheduledHoursColumn } from "./useScheduledHoursColumn";
import { useSelectionColumn } from "./useSelectionColumn";
import { useTotalColumn } from "./useTotalColumn";

export const useColumns = ({
	user,
	canCreate,
	canEdit,
	dateIdMap,
	dateTimesheetMap,
	viewSummary,
	edit,
	view,
	remove,
	approve,
	unapprove,
	lock,
	unlock,
	copy,
	scheduleInfo,
	clockExceptions,
	isMyTimesheet = false,
	waive,
	cancelWaive,
	openMissPunchModal,
	openInOutDateModal,
	canViewRates,
}) => {
	const { hasAccess: canViewHistory } = useAccess(
		isMyTimesheet
			? "timeSheet.canViewHistoryMyTimesheet"
			: "timeSheet.canViewHistory"
	);

	const { hasAccess: canViewExceptions } = useAccess(
		isMyTimesheet
			? "timeSheet.canViewClockExceptionsMyTimesheet"
			: "timeSheet.canViewClockExceptions"
	);

	const { hasAccess: canCreateMissPunch } = useAccess("requests.timesheet.missPunch.canCreate");
	const { hasAccess: canCreateMealWaive } = useAccess("requests.timesheet.mealPremiumWaive.canCreate");
	const canCreateRequest = canCreateMissPunch || canCreateMealWaive;

	const addColumn = useAddColumn({ dateIdMap, dateTimesheetMap });
	const selectionColumn = useSelectionColumn({ user, dateIdMap, dateTimesheetMap });
	const dateGroupActionColumn = useDateGroupActionColumn({
		dateIdMap,
		dateTimesheetMap,
		approve,
		unapprove,
		lock,
		unlock,
		copy,
	});

	const dateColumn = useDateColumn({ dateTimesheetMap, view, viewSummary, waive, cancelWaive, isMyTimesheet });
	const inColumn = useInColumn({ user, canEdit, clockExceptions, view: canViewExceptions && view, openInOutDateModal });
	const outColumn = useOutColumn({
		user,
		canEdit,
		clockExceptions,
		view:
			canViewExceptions && view,
		openInOutDateModal,
		openMissPunchModal: canCreateRequest ? openMissPunchModal : undefined,
		isMyTimesheet,
	});
	const overnightColumn = useOvernightColumn();
	const rateColumn = useRateColumn({ canEdit });
	const payCodeColumn = usePayCodeColumn({ canEdit, user, isMyTimesheet });
	const payCodeDescriptionColumn = usePayCodeDescriptionColumn({ canEdit, user });
	const hourTypeColumn = useHourTypeColumn();
	const durationColumn = useDurationColumn({ canEdit });
	const otherDurationColumn = useOtherDurationColumn({ canEdit, canViewRates });
	const dateDurationColumn = useDateDurationColumn({ dateIdMap, dateTimesheetMap });
	const dateTotalColumn = useDateTotalColumn({ dateIdMap, dateTimesheetMap });
	const totalColumn = useTotalColumn({ canEdit, canViewRates });
	const locationColumn = useLocationColumn({ canEdit });
	const jobColumn = useJobColumn({ canEdit });
	const groupColumns = useGroupsColumns({ canEdit });
	const customFieldsColumns = useCustomFieldsColumns({ canEdit });
	const actionsColumn = useActionsColumn({ edit, remove, isMyTimesheet });
	const scheduledHoursColumn = useScheduledHoursColumn({ dateIdMap, dateTimesheetMap, scheduleInfo, view: canViewHistory && view });
	const scheduledAmountColumn = useScheduledAmountColumn({ dateTimesheetMap, scheduleInfo });

	return useMemo(
		() =>
			[
				canCreate ? addColumn : null,
				selectionColumn,
				dateGroupActionColumn,
				dateColumn,
				inColumn,
				outColumn,
				overnightColumn,
				...(canViewRates ? [rateColumn] : []),
				payCodeColumn,
				payCodeDescriptionColumn,
				hourTypeColumn,
				durationColumn,
				otherDurationColumn,
				dateDurationColumn,
				scheduledHoursColumn,
				totalColumn,
				...(canViewRates ? [dateTotalColumn, scheduledAmountColumn] : []),
				locationColumn,
				jobColumn,
				...groupColumns,
				...customFieldsColumns,
				(edit || remove || (openMissPunchModal && canCreateRequest)) ? actionsColumn : null,
			]?.filter((c) => !!c),
		[
			canCreate,
			addColumn,
			selectionColumn,
			dateGroupActionColumn,
			dateColumn,
			inColumn,
			outColumn,
			rateColumn,
			durationColumn,
			otherDurationColumn,
			payCodeColumn,
			payCodeDescriptionColumn,
			hourTypeColumn,
			overnightColumn,
			totalColumn,
			dateTotalColumn,
			dateDurationColumn,
			locationColumn,
			jobColumn,
			groupColumns,
			customFieldsColumns,
			actionsColumn,
			scheduledHoursColumn,
			edit,
			remove,
			scheduledAmountColumn,
			openMissPunchModal,
			canCreateRequest,
			canViewRates,
		],
	);
};
